import { useState, useEffect } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Grid,
    Paper,
    Chip,
    Typography,
    ListItem
} from '@material-ui/core';
import { Table } from 'rsuite';
import {
    CheckCircle,
    CheckCircleOutline,
    Visibility
} from '@material-ui/icons';
// eslint-disable-next-line import/no-unresolved
import { dynamicSort } from '@commonClient2/services/sort';
import LockAwardingConfirmation from '../../components/LockAwardingConfirmation';
import { selectedBiddedProfile, lockAwardedShiftline, unlockAwardedShiftline } from '../../store/bidProfile';
import AssignedShiftlinePanel from './AssignedShiftlinePanel';
import getSortIcon from '../SortIcon';
import './AssignedShiftlineSelection.scss';

const { Column, HeaderCell, Cell } = Table;

const widthMobileToggle = 993;

/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

/**
 * @param {{search: string}} props
 */
function AssignedShiftlineSelection(props) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'employee.bidAssignationShiftline' });
    const { t: tCommon } = useTranslation('shiftBidding', { keyPrefix: 'common' });
    const dispatch = useDispatch();

    const { search } = props;

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeAppData } = useSelector((state) => state);
    const { assignedShiftlines } = employeeAppData;

    const [filteredAssignments, setFilteredAssignments] = useState(assignedShiftlines);
    const [sortColumn, setSortColumn] = useState('name');
    /** @type {[SortType, Function]} */
    const [sortType, setSortType] = useState(defaultSortType);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [showProtectModal, setShowProtectModal] = useState(false);
    /** @type {[EmployeeAssignedShiftline.IEmployeeAssignedShiftline|null, Function]} */
    const [selectedRow, setSelectedRow] = useState(null);
    const [windowWidth, setWindowWidth] = useState(0);
    const isMobile = windowWidth < widthMobileToggle;
    /**
     * @param {string} newSortColumn
     * @param {SortType|undefined} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        setSortType(newSortType);
    };

    /**
     * @param {string} dataKey
     * @param {string} type
     * @param {EmployeeAssignedShiftline.IEmployeeAssignedShiftline[] | null} data
     */
    const handleSort = (dataKey, type, data = null) => {
        if (data === null) {
            data = filteredAssignments;
        }
        /** @type {EmployeeAssignedShiftline.IEmployeeAssignedShiftline[]} */
        let sortedFilteredData = [];
        const dynamicSortOrder = type === 'desc' ? '-' : '';

        sortedFilteredData = data.sort(
            dynamicSort(`${dynamicSortOrder}${dataKey}`),
        );
        setFilteredAssignments(_.cloneDeep(sortedFilteredData));
    };

    /**
     * @param {EmployeeAssignedShiftline.IEmployeeAssignedShiftline} rowData
     */
    const handleView = async (rowData) => {
        await dispatch(selectedBiddedProfile(rowData.sourceProfileId, false));
        setIsDrawerOpen(true);
    };

    /**
     * @param {EmployeeAssignedShiftline.IEmployeeAssignedShiftline} rowData
     */
    const handleLockClick = (rowData) => {
        setSelectedRow(rowData);
        setShowProtectModal(true);
    };

    const confirmProtectAssignedShiftline = () => {
        // @ts-ignore
        if (selectedRow?.isLocked) {
            // @ts-ignore
            dispatch(unlockAwardedShiftline(selectedRow.sourceProfileId, false));
        } else {
            // @ts-ignore
            dispatch(lockAwardedShiftline(selectedRow.sourceProfileId, false));
        }
        setShowProtectModal(false);
    };

    const renderProtectRebidButton = (rowData) => (
        rowData.isLockable && (
            rowData.isLocked
                ? (
                    <Button
                        className="protected-icon"
                        // @ts-ignore
                        onClick={(event) => handleLockClick(rowData)}
                    >
                        <div className="background">
                            <CheckCircle className="icon" />
                        </div>
                    </Button>
                )
                : (
                    <Button
                        className="unprotected-icon"
                        // @ts-ignore
                        onClick={(event) => handleLockClick(rowData)}
                    >
                        <CheckCircleOutline className="icon" />
                    </Button>
                )
        )
    );

    const renderViewButton = (rowData) => (
        <div className={isMobile ? '' : 'core--action-button-container'}>
            <Button
                // @ts-ignore
                onClick={() => handleView(rowData)}
                className="employee-bid-assignation-tablerow-action-button"
            >
                <Visibility fontSize="inherit" />
                <div className="view-button-label">View</div>
            </Button>
        </div>
    );

    useEffect(() => {
        if (search) {
            const filtered = assignedShiftlines.filter((x) => {
                const searchString = `${x.name}
                    |${x.schedule}
                    |${x.period}
                    |${x.lockUpdateDate == null ? '-' : x.lastUpdateString}`
                    .toLocaleLowerCase();
                return searchString.includes(search.toLocaleLowerCase());
            });
            setFilteredAssignments(filtered);
        } else {
            setFilteredAssignments(assignedShiftlines);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(assignedShiftlines), search]);

    useEffect(() => {
        handleSort(sortColumn, sortType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortColumn, sortType]);

    useEffect(() => {
        handleSort(sortColumn, sortType, assignedShiftlines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(assignedShiftlines)]);

    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    const renderDesktopView = () => (
        <Grid
            container
            justifyContent="space-between"
            className="assignations-grid-container"
        >
            <Grid
                item
                xs={12}
                lg={12}
            >
                <Paper 
                    elevation={2} 
                    className="assigned-shiftline-selection-table-container"
                >
                    <Table
                        key="employee-bid-template-table"
                        data={filteredAssignments}
                        id="employee-bid-template-table"
                        rowClassName="employee-assignation-table-row"
                        sortColumn={sortColumn}
                        sortType={sortType}
                        onSortColumn={handleSortColumn}
                        fillHeight
                    >
                        <Column
                            sortable
                            flexGrow={2}
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.name')}
                            </HeaderCell>
                            <Cell dataKey="name">
                                {(rowData) => (
                                    <>
                                        {rowData.name}
                                        {
                                            rowData.rebidNumber > 0
                                                && (
                                                    <Chip
                                                        label={tCommon('rebidNumber', { number: rowData.rebidNumber })}
                                                        variant="outlined"
                                                        className="blnc-rebid-chip"
                                                    />
                                                )
                                        }
                                    </>
                                )}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.schedule')}
                            </HeaderCell>
                            <Cell dataKey="schedule" />
                        </Column>
                        <Column
                            flexGrow={1}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.period')}
                            </HeaderCell>
                            <Cell dataKey="period" />
                        </Column>
                        <Column
                            flexGrow={1}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.bidLock')}
                            </HeaderCell>
                            <Cell
                                className="rebidProtected"
                                dataKey="isLocked"
                            >
                                { (rowData) => renderProtectRebidButton(rowData) }
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={1}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.bidLockUpdate')}
                            </HeaderCell>
                            <Cell dataKey="lastUpdateString" />
                        </Column>
                        <Column
                            flexGrow={1}
                            fixed="right"
                        >
                            <HeaderCell> </HeaderCell>
                            <Cell>
                                { (rowData) => renderViewButton(rowData) }
                            </Cell>
                        </Column>
                    </Table>
                </Paper>
            </Grid>
        </Grid>
    );

    const renderMobileView = () => (
        <div className="asls-mobile">
            <Table
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
                height={40}
            >
                <Column
                    fixed="left"
                    flexGrow={5}
                    sortable
                >
                    <HeaderCell>{t('tableHeaders.name')}</HeaderCell>
                    <Cell dataKey="name" />
                </Column>
                <Column
                    fixed="right"
                    flexGrow={2}
                    sortable
                >
                    <HeaderCell>{t('tableHeaders.bidLock')}</HeaderCell>
                    <Cell dataKey="isLocked" />
                </Column>
            </Table>
            {
                filteredAssignments.map((x) => (
                    <ListItem
                        className="asls-card-button-root"
                        key={`ListItem_Scheduled_bidProfileItem_${x.sourceProfileId}`}
                    >
                        <Grid
                            container
                            xs={12}
                            className="dated-card-header-container"
                        >
                            <Grid
                                item
                                xs={10}
                                className="dated-card-header-container"
                            >
                                <Typography
                                    variant="h6"
                                    className="dated-card-header-title"
                                >
                                    {x.name}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                xs={1}
                                className="dated-card-header-container"
                            >
                                { renderProtectRebidButton(x) }
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                className="dated-card-header-container"
                            >
                                <Typography
                                    variant="body2"
                                    gutterBottom
                                >
                                    {`${t('tableHeaders.schedule')} :`}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <Typography
                                    variant="body2"
                                    className="dated-card-header-subtitle"
                                    gutterBottom
                                >
                                    {
                                        x.schedule
                                    }
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={6}
                                className="dated-card-header-container"
                            >
                                <Typography
                                    variant="body2"
                                    gutterBottom
                                >
                                    {`${t('tableHeaders.period')} :`}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <Typography
                                    variant="body2"
                                    className="dated-card-header-subtitle"
                                    gutterBottom
                                >
                                    {
                                        x.period
                                    }
                                </Typography>
                            </Grid>
                            {renderViewButton(x)}
                        </Grid>
                    </ListItem>
                ))
            }
        </div>
    );

    return (
        <>
            {isMobile ? renderMobileView() : renderDesktopView()}
            <AssignedShiftlinePanel
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                isMobile={isMobile}
            />
            <LockAwardingConfirmation
                isOpen={showProtectModal}
                setIsOpen={setShowProtectModal}
                onConfirm={confirmProtectAssignedShiftline}
                // @ts-ignore
                isLocked={selectedRow?.isLocked}
            />
        </>
    );
}

export default AssignedShiftlineSelection;
